import { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { getFeatureFlag } from 'components/api';
import { HUB_FEATURE_FLAG, HUB_FLAG_ENABLED } from 'components/buyer/hub/featureFlag';

export type BuyerHubFlagStatus = 'loading' | 'enabled' | 'disabled';

// The buyer-hub rollout flag gates several independently-mounted header surfaces
// (the desktop account dropdown, the desktop nav Dashboard link, the mobile menu,
// and the nav-transactions prefetch). Cache the resolved variant per customer
// grain so those surfaces share a single BFF read instead of each firing their
// own. Only ever written client-side from `useEffect`, so module state shared
// across SSR requests can't leak between users (the fetch never runs on server).
const resolvedByGrain = new Map<string, boolean>();
const inflightByGrain = new Map<string, Promise<boolean>>();

const resolveHubFlag = (customerUuid: string): Promise<boolean> => {
  const cached = resolvedByGrain.get(customerUuid);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }
  const inflight = inflightByGrain.get(customerUuid);
  if (inflight) {
    return inflight;
  }
  const request = getFeatureFlag(HUB_FEATURE_FLAG, customerUuid, { defaultValue: 'control' })
    .then((variant) => {
      const enabled = variant === HUB_FLAG_ENABLED;
      resolvedByGrain.set(customerUuid, enabled);
      inflightByGrain.delete(customerUuid);
      return enabled;
    })
    .catch(() => {
      inflightByGrain.delete(customerUuid);
      return false;
    });
  inflightByGrain.set(customerUuid, request);
  return request;
};

/**
 * Resets the module-level flag cache. Exported for tests (and HMR) to isolate
 * state between runs; not used by application code.
 */
export const __resetBuyerHubFlagCacheForTest = () => {
  resolvedByGrain.clear();
  inflightByGrain.clear();
};

/**
 * Resolves the buyer-hub rollout flag for the authenticated customer.
 *
 * Returns a tri-state so callers can distinguish "still resolving" from
 * "resolved to off": the redesigned account navigation only renders for enrolled
 * users, and the legacy navigation must not flash (and fire its data queries)
 * while the flag is in flight. The flag is only evaluated for authenticated users
 * because every gated surface is logged-in only.
 */
export const useBuyerHubFlagStatus = (): BuyerHubFlagStatus => {
  const { authentication } = useAuth();
  const [status, setStatus] = useState<BuyerHubFlagStatus>('loading');

  const authState = authentication.state;
  const customerUuid =
    authentication.state === 'authenticated' ? (authentication.user?.customerUuid ?? '') : '';

  useEffect(() => {
    if (authState === 'authenticating') {
      setStatus('loading');
      return;
    }
    if (authState !== 'authenticated') {
      setStatus('disabled');
      return;
    }
    // Authenticated, but the customer UUID hasn't populated yet. Resolving the
    // flag on an empty grain would fall back to the BFF default (control) and
    // transiently flash the legacy menu — and fire its data queries — before the
    // real UUID arrives. Stay loading; the effect re-runs once customerUuid is
    // present (it is in the dep array).
    if (!customerUuid) {
      setStatus('loading');
      return;
    }

    let isActive = true;
    setStatus('loading');
    resolveHubFlag(customerUuid).then((enabled) => {
      if (isActive) {
        setStatus(enabled ? 'enabled' : 'disabled');
      }
    });
    return () => {
      isActive = false;
    };
  }, [authState, customerUuid]);

  return status;
};
